// imports
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, CardActionArea, CardMedia, CardHeader } from '@mui/material';

// local imports
import { BASEMAPS, BASEMAP_OPTION } from '../../../constants/BasemapOptions';
import { useUrlParameters } from '../../../hooks/useUrlParameters';
import { mapActions, RootState } from '../../../store';

interface BasemapOptionProps {
  identifier: string;
}

/**
 * BasemapOption
 *
 * Renders a card with a thumbnail and title for a basemap option.
 * When clicked, it sets the selected basemap in the Redux store and updates URL.
 *
 * @param {string} identifier - The identifier of the basemap option.
 * @returns {JSX.Element} The rendered BasemapOption component.
 */
const BasemapOption: React.FC<BasemapOptionProps> = ({ identifier }) => {
  const dispatch = useDispatch();
  const { setParameter } = useUrlParameters();
  const activeBasemap = useSelector((state: RootState) => state.map?.basemap);
  const basemap = BASEMAPS.find(
    (basemap: BASEMAP_OPTION) => basemap.identifier === identifier
  );

  // Handle click event to change the basemap
  const handleChangeBasemap = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (basemap) {
      dispatch(mapActions.setBasemap(basemap));
      // Update URL parameter
      setParameter('basemap', basemap.identifier);
    }
  };

  return (
    <Card
      key={identifier}
      data-testid={`basemap-option-${identifier}`}
      sx={{
        minWidth: 100,
        maxWidth: 120,
        '@media (min-width: 650px)': {
          minWidth: 'unset',
          maxWidth: 'unset',
          width: 170,
        },
      }}
    >
      <CardActionArea
        onClick={handleChangeBasemap}
        aria-label={identifier === 'No Basemap' ? 'No Base map' : undefined}
        data-active={
          basemap?.identifier === activeBasemap?.identifier ? '' : undefined
        }
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          backgroundColor: 'action.selected',
          height: '100%',
          border: '2px solid transparent',
          '&[data-active]': {
            backgroundColor: '#D9E8F6',
            '&:hover': {
              backgroundColor: 'action.selectedHover',
            },
            borderColor: 'primary.main',
          },
        }}
      >
        <CardMedia
          component="img"
          image={basemap?.thumbnail}
          alt={`${identifier}-thumbnail`}
          width={150}
          height={100}
          sx={{
            width: '100%',
            maxWidth: 120,
            height: 'auto',
            px: 1,
            pt: 1,
            '@media (min-width: 650px)': {
              width: 150,
              height: 100,
              objectFit: 'cover',
              ml: 1,
              mr: 1,
              mt: 1,
              maxWidth: 'unset',
              px: 0,
              pt: 0,
            },
          }}
        />
        <CardHeader
          title={identifier}
          sx={{
            mx: 1,
            '&.MuiCardHeader-root': {
              px: 0,
              py: 0.5,
            },
            textAlign: 'center',
            '& .MuiCardHeader-content': {
              '& .MuiTypography-root': {
                fontSize: '0.85rem',
                '@media (min-width: 650px)': {
                  fontSize: '1rem'
                }

              },
              '[data-active] &': {
                color: '#124A82', // blue highlight when parent is active
              },
            },
          }}
        />
      </CardActionArea>
    </Card>
  );
};

export default BasemapOption;
